import { Checkbox, Tag } from '@ads/components-react';

import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DataTable } from '@/shared/ui/organisms/DataTable';

import type { SelectableQuestion } from '../../../hooks/useExamQuestionSelection';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do QuestionSelectionTable */
export interface QuestionSelectionTableProps {
  /** Lista de questões retornadas pela busca */
  readonly data: SelectableQuestion[];
  /** Total de registros para paginação server-side */
  readonly totalCount: number;
  /** Página atual (1-indexed) */
  readonly currentPage: number;
  /** Total de páginas */
  readonly totalPages: number;
  /** Estado de carregamento */
  readonly isLoading: boolean;
  /** IDs das questões atualmente selecionadas */
  readonly selectedIds: ReadonlySet<string>;
  /** Callback ao marcar/desmarcar uma questão */
  readonly onToggle: (question: SelectableQuestion) => void;
  /** Callback ao mudar a página */
  readonly onPageChange: (page: number) => void;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const columnHelper = createColumnHelper<SelectableQuestion>();

const STATUS_TAG: Record<string, 'positive' | 'helper' | 'negative' | 'default'> = {
  Ativo: 'positive',
  Inativo: 'default',
};

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Tabela de seleção de questões da biblioteca para uso no wizard de avaliações.
 *
 * Exibe colunas de enunciado, disciplina, autor, tipo e status, com checkbox
 * de seleção em cada linha. Suporta paginação server-side.
 *
 * @example
 * ```tsx
 * <QuestionSelectionTable
 *   data={data}
 *   totalCount={totalCount}
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   isLoading={isLoading}
 *   selectedIds={selectedIds}
 *   onToggle={toggleQuestion}
 *   onPageChange={page => updateFilters({ page })}
 * />
 * ```
 */
export function QuestionSelectionTable({
  data,
  totalCount,
  currentPage,
  totalPages,
  isLoading,
  selectedIds,
  onToggle,
  onPageChange,
}: QuestionSelectionTableProps) {
  const { t } = useTranslation('examManagement');

  const columns = useMemo(
    () =>
      [
        // Checkbox de seleção
        columnHelper.display({
          id: 'select',
          header: () => null,
          cell: ({ row }) => (
            <Checkbox
              checked={selectedIds.has(row.original.id)}
              size="sm"
              onCheckedChange={checked => {
                if (checked !== 'indeterminate') {
                  onToggle(row.original);
                }
              }}
            />
          ),
        }),

        // Enunciado
        columnHelper.accessor('text', {
          header: t('questions.selection.columns.question'),
          cell: info => (
            <span
              dangerouslySetInnerHTML={{ __html: info.getValue() }}
              className="block max-w-[280px] truncate ts-paragraph-xxs text-ctx-content-title"
            />
          ),
        }),

        // Disciplina
        columnHelper.accessor('discipline', {
          header: t('questions.selection.columns.discipline'),
          cell: info => (
            <span className="line-clamp-1 ts-paragraph-xxs text-ctx-content-base">
              {info.getValue()}
            </span>
          ),
        }),

        // Autor
        columnHelper.accessor('author', {
          header: t('questions.selection.columns.author'),
          cell: info => (
            <span className="line-clamp-1 ts-paragraph-xxs text-ctx-content-base">
              {info.getValue()}
            </span>
          ),
        }),

        // Tipo
        columnHelper.accessor('type', {
          header: t('questions.selection.columns.type'),
          cell: info => (
            <span className="text-nowrap ts-paragraph-xxs text-ctx-content-base">
              {info.getValue()}
            </span>
          ),
        }),

        // Status
        columnHelper.accessor('status', {
          header: t('questions.selection.columns.status'),
          cell: info => (
            <Tag
              status={STATUS_TAG[info.getValue()] ?? 'default'}
              label={info.getValue()}
              size="sm"
              custom={{ text: 'text-nowrap' }}
            />
          ),
        }),
      ] as ColumnDef<SelectableQuestion, unknown>[],
    [t, selectedIds, onToggle],
  );

  return (
    <DataTable
      data={data}
      columns={columns}
      isLoading={isLoading}
      totalCount={totalCount}
      currentPage={currentPage}
      totalPages={totalPages}
      emptyStateMessage={t('questions.selection.empty')}
      onPageChange={onPageChange}
      skeletonRowCount={10}
    />
  );
}
